/**
 * DigitalScholarCard — the "My Player" header on the student dashboard. Shows the
 * scholar-athlete identity (avatar, name, grade, city, sport/position), Overall
 * Scholar Rating, active streak and XP level. Supports photo upload (client-resized),
 * quick profile edit, and Download Official SAA ID (PDF).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Star, Flame, Zap, Download, Pencil, Upload, Loader2, MapPin, Trophy, X, Award } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const resizeToDataUrl = (file, size = 256) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      const m = Math.min(img.width, img.height);
      ctx.drawImage(img, (img.width - m) / 2, (img.height - m) / 2, m, m, 0, 0, size, size);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = reject; img.src = e.target.result;
  };
  reader.onerror = reject; reader.readAsDataURL(file);
});

const DigitalScholarCard = () => {
  const [card, setCard] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ city: '', sport: '', position: '' });
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const r = await api.get('/school/scholar-card', { headers: authHeaders() }); setCard(r.data.card); }
    catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const openEdit = () => { setForm({ city: card.city || '', sport: card.sport || '', position: card.position || '' }); setEditing(true); };

  const saveProfile = async () => {
    setBusy(true);
    try { await api.post('/school/profile', form, { headers: authHeaders() }); toast.success('Profile updated'); setEditing(false); load(); }
    catch { toast.error('Could not save'); } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await resizeToDataUrl(file);
      await api.post('/school/avatar', { image_data: dataUrl }, { headers: authHeaders() });
      toast.success('Avatar updated'); load();
    } catch { toast.error('Could not upload'); } finally { setBusy(false); }
  };

  const downloadId = async () => {
    setDl(true);
    try {
      const r = await api.get('/school/scholar-id-pdf', { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `SAA_ID_${card.student_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download ID'); } finally { setDl(false); }
  };

  if (!card) return null;
  const pct = Math.round((card.xp_into_level / card.level_step) * 100);
  const initial = (card.name || 'S').trim()[0]?.toUpperCase() || 'S';

  return (
    <div className="mb-6 border-2 border-orange-500/40 bg-gradient-to-br from-zinc-900 to-black overflow-hidden" data-testid="digital-scholar-card">
      <div className="flex flex-col sm:flex-row">
        {/* Avatar + rating rail */}
        <div className="relative shrink-0 bg-black/40 p-5 flex sm:flex-col items-center gap-4 sm:w-52">
          <button onClick={() => fileRef.current?.click()} className="relative group" data-testid="scholar-avatar-btn">
            <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-orange-500 bg-orange-500/20 flex items-center justify-center">
              {card.avatar_url
                ? <img src={card.avatar_url} alt="avatar" className="w-full h-full object-cover" />
                : <span className="text-4xl font-black text-orange-400">{initial}</span>}
            </div>
            <div className="absolute inset-0 rounded-2xl bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              {busy ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Upload className="w-5 h-5 text-white" />}
            </div>
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="scholar-avatar-input" />
          <div className="text-center">
            <div className="text-5xl font-black text-orange-500 leading-none" data-testid="scholar-rating">{card.overall_rating}</div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-black mt-1">Scholar Rating</div>
          </div>
        </div>

        {/* Identity */}
        <div className="flex-1 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-400 font-black">Digital Scholar Card</div>
              <h2 className="text-2xl font-black text-white leading-tight" data-testid="scholar-name">{card.name}</h2>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-400 mt-1">
                <span className="font-bold text-white">Grade {card.grade_level}</span>
                {card.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{card.city}</span>}
                {card.sport && <span className="flex items-center gap-1"><Trophy className="w-3 h-3 text-orange-500" />{card.sport}{card.position ? ` · ${card.position}` : ''}</span>}
                <span className="font-mono text-zinc-600">#{card.student_id}</span>
              </div>
            </div>
            <button onClick={openEdit} className="p-2 border-2 border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-white transition-colors" data-testid="scholar-edit-btn"><Pencil className="w-3.5 h-3.5" /></button>
          </div>

          <div className="grid grid-cols-3 gap-2 mt-4">
            <div className="border-2 border-zinc-800 p-2.5 text-center">
              <Flame className="w-4 h-4 text-orange-500 mx-auto" />
              <div className="text-xl font-black text-white mt-1" data-testid="scholar-streak">{card.streak_days}</div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-black">Day Streak</div>
            </div>
            <div className="border-2 border-zinc-800 p-2.5 text-center">
              <Zap className="w-4 h-4 text-emerald-400 mx-auto" />
              <div className="text-xl font-black text-white mt-1" data-testid="scholar-level">Lv {card.level}</div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-black">{card.xp} XP</div>
            </div>
            <div className="border-2 border-zinc-800 p-2.5 text-center">
              <Star className="w-4 h-4 text-yellow-400 mx-auto" />
              <div className="text-xl font-black text-white mt-1">{card.overall_rating}</div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-black">Rating</div>
            </div>
          </div>

          <div className="mt-3">
            <div className="flex justify-between text-[10px] text-zinc-500 font-bold mb-1"><span>Level {card.level}</span><span>{card.xp_to_next} XP to Lv {card.level + 1}</span></div>
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-orange-600 to-orange-400 transition-all" style={{ width: `${pct}%` }} /></div>
          </div>

          {card.elective_badges?.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="scholar-elective-badges">
              <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-black">Elective Stamps</span>
              {card.elective_badges.map((b) => (
                <span key={b.key} className="flex items-center gap-1 border-2 border-amber-500/50 bg-amber-500/10 px-2 py-1" title={b.name} data-testid={`scholar-elective-badge-${b.key}`}>
                  <Award className="w-3 h-3 text-amber-400" /><span className="text-[9px] font-black uppercase tracking-wider text-amber-300">{b.name}</span>
                </span>
              ))}
              {card.elective_credits?.length > 0 && (
                <span className="text-[9px] text-zinc-500 font-bold">· {card.elective_credits.reduce((s, c) => s + (c.amount || 0), 0)} Elective Credits</span>
              )}
            </div>
          )}

          <button onClick={downloadId} disabled={dl} className="mt-4 flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 text-[10px] font-black tracking-[0.2em] uppercase transition-colors" data-testid="download-id-btn">
            {dl ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download Official SAA ID
          </button>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setEditing(false)}>
          <div className="bg-zinc-950 border-2 border-zinc-800 p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="scholar-edit-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="text-sm font-black uppercase tracking-widest text-white">Edit Player Profile</h3><button onClick={() => setEditing(false)}><X className="w-4 h-4 text-zinc-500" /></button></div>
            <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Home City</label>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-orange-500 mb-3" data-testid="edit-city" />
            <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Primary Sport</label>
            <input value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-orange-500 mb-3" data-testid="edit-sport" />
            <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Position</label>
            <input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-orange-500 mb-4" data-testid="edit-position" />
            <button onClick={saveProfile} disabled={busy} className="w-full bg-orange-600 hover:bg-orange-500 text-white py-2.5 text-xs font-black tracking-[0.2em] uppercase transition-colors" data-testid="save-profile-btn">{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalScholarCard;
